// src/modules/games/components/Show/ChallengeResults.js
import React, { useMemo } from "react";
import styled from "styled-components";
// Assuming Buttonz and BodyCode are exported from Duels.js
import { Buttonz, BodyCode } from "./Duels"; 

// --- Utility: 30-Day Filter ---
const THIRTY_DAYS_AGO = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

// --- Styled Components ---

const ResultsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 90%;
    max-width: 1200px;
    padding: 20px;
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 10px;
    border-bottom: 2px solid #af9dc4;
`;

const Title = styled.h1`
    color: #673c8c;
    font-size: 2.5em;
    margin: 0;
`;

const SummaryBox = styled.div`
    background: linear-gradient(180deg, #673c8c, #543171);
    color: white;
    padding: 20px;
    border-radius: 10px;
    text-align: center;
    flex-shrink: 0;
    width: 300px; 
    
    h3 {
        margin: 0 0 10px 0;
        font-size: 2.5em;
    }
    .win-count {
        font-size: 3em;
        font-weight: bold;
    }
`;

const ResultsContent = styled.div`
    display: flex;
    gap: 20px;
    align-items: flex-start;
`;

const TableFrame = styled.div`
    flex: 1;
    background: #ffffff;
    border: 2px solid #673b8b;
    border-radius: 1em;
    overflow: hidden;
    height: 400px; /* Fixed height for scrollbar */
    display: flex;
    flex-direction: column;
`;

const ResultsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed; /* FIX: Added for consistent column width across header and body */
    
    thead tr {
        display: table; /* FIX: Added to match tbody tr display type */
        width: 100%;
        table-layout: fixed;
    }
    
    thead th {
        background-color: #af9dc4;
        color: #543171;
        padding: 10px;
        text-align: left;
        position: sticky; 
        top: 0;
        z-index: 10;
        font-size: 3em; 
    }
    
    tbody {
        display: block;
        height: 350px; /* Body height to enable scrollbar */
        overflow-y: auto;
    }
    
    tbody tr {
        display: table;
        width: 100%;
        table-layout: fixed; /* Ensures columns align */
    }

    td {
        padding: 10px;
        border-bottom: 1px solid #eee;
        font-size: 2.5em;
    }
    
    .win { color: green; font-weight: bold; }
    .loss { color: red; font-weight: bold; }
    .draw { color: #f28c38; font-weight: bold; }
`;

// --- Helper Function to Get Opponent Username ---
const getOpponentUsernameFromDuel = (duel, currentUserId) => {
    if (!duel.pupils || duel.pupils.length < 2) {
        const opponentId = duel.player1Id === currentUserId ? duel.player2Id : duel.player1Id;
        return `User ${opponentId || 'Unknown'}`;
    }
    const opponent = duel.pupils.find(p => p.id !== currentUserId);
    return opponent ? opponent.username : 'Unknown Opponent';
};

// --- Component ---

const ChallengeResults = ({ game, onBack, data }) => {
    const { duels, currentUserId } = data; 

    const processedDuels = useMemo(() => {
        if (!duels || !currentUserId) return { results: [], winCount: 0 };

        const results = [];
        let winCount = 0;

        duels.forEach(duel => {
            // Must be complete
            if (!duel.is_complete) return; 
            
            // Must be within 30 days
            const duelDate = new Date(duel.completed_at || duel.updated_at); 
            if (duelDate < THIRTY_DAYS_AGO) return; 
            
            // REMOVED: The game/galaxy filter: 
            // if (duel.galaxy_id !== game.id) return;

            
            let result = 'Incomplete'; 

            // Logic to determine result by score comparison
            const userScore = duel.scores?.find(s => s.pupil_id === currentUserId);
            const opponentScore = duel.scores?.find(s => s.pupil_id !== currentUserId);

            // Only proceed if both scores are present
            if (userScore && opponentScore) {
                if (userScore.correct > opponentScore.correct) {
                    result = 'Win';
                } else if (userScore.correct < opponentScore.correct) {
                    result = 'Loss';
                } else {
                    // Correct scores are equal
                    result = 'Draw'; 
                }
            }

            // Only push duels with a final result status (Win, Loss, or Draw)
            if (result !== 'Incomplete') {
                if (result === 'Win') {
                    winCount++;
                }

                results.push({
                    id: duel.id,
                    duelData: duel, 
                    result,
                    date: duelDate,
                });
            }
        });

        results.sort((a, b) => b.date.getTime() - a.date.getTime());

        return { results, winCount };
    }, [duels, currentUserId]); // Removed 'game.id' from dependencies
    
    const { results, winCount } = processedDuels;
    
    const ResultRow = ({ duel }) => {
        const opponentUsername = getOpponentUsernameFromDuel(duel.duelData, currentUserId);
        
        return (
            <tr>
                <td style={{ width: '50%' }}>{opponentUsername}</td>
                <td style={{ width: '50%' }} className={duel.result.toLowerCase()}>
                    {duel.result}
                </td>
            </tr>
        );
    };

    return (
        <BodyCode>
            <ResultsContainer>
                {/* Header and Back Button - Title is updated */}
                <HeaderContainer>
                    {/* FIX: Title updated to reflect all subjects are shown */}
                    <Title>Duel Results - Last 30 Days (All Subjects)</Title>
                    <Buttonz onClick={onBack}>Back to Duels</Buttonz>
                </HeaderContainer>

                <ResultsContent>
                    {/* Left Side: Results Table */}
                    <TableFrame>
                        <ResultsTable>
                            <thead>
                                <tr>
                                    <th style={{ width: '50%' }}>Opponent</th>
                                    <th style={{ width: '50%' }}>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.length === 0 ? (
                                    <tr>
                                        <td colSpan="2" style={{ textAlign: 'center', color: '#af9dc4' }}>
                                            No completed duels in the last 30 days.
                                        </td>
                                    </tr>
                                ) : (
                                    results.map(duel => <ResultRow key={duel.id} duel={duel} />)
                                )}
                            </tbody>
                        </ResultsTable>
                    </TableFrame>

                    {/* Right Side: Summary Box */}
                    <SummaryBox>
                        <h3>Duel Wins in 30 days</h3>
                        <div className="win-count">{winCount}</div>
                    </SummaryBox>
                </ResultsContent>
            </ResultsContainer>
        </BodyCode>
    );
};

export default ChallengeResults;